import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { asArray } from "@/lib/utils";
import { useAssignKidToBatch } from "@/hooks/useAssignKidToBatch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BatchCombobox } from "@/components/batches/BatchCombobox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BatchOption {
  id: number;
  name: string;
}

interface AssignBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kidId: number;
  kidFirstName: string;
  currentBatchId: number | null;
}

// Same picker + confirm-on-move flow as CoachKidDetail.tsx's inline batch
// picker, wrapped in a Dialog for table-row trigger points (players-page.tsx)
// that don't have room for an inline Select. Shares useAssignKidToBatch —
// no separate assignment/mutation logic. GET /api/players doesn't resolve a
// batch name (only batchId), unlike GET /api/players/:id/sessions which
// CoachKidDetail.tsx reads from — so this component resolves the current
// batch's name itself from its own batches fetch instead of requiring the
// caller to supply one.
export function AssignBatchDialog({
  open,
  onOpenChange,
  kidId,
  kidFirstName,
  currentBatchId,
}: AssignBatchDialogProps) {
  const { data: batchesData } = useQuery({
    queryKey: ["/api/batches"],
    queryFn: async () => apiRequest("GET", "/api/batches"),
    enabled: open,
  });
  const batches: BatchOption[] = asArray<BatchOption>(batchesData?.data);
  const currentBatchName = batches.find((b) => b.id === currentBatchId)?.name ?? null;

  const assignKid = useAssignKidToBatch();
  const [pendingBatchId, setPendingBatchId] = useState<number | null>(null);

  function handleChange(value: string) {
    const newBatchId = Number(value);
    if (currentBatchId) {
      // Already assigned somewhere — confirm before moving.
      setPendingBatchId(newBatchId);
    } else {
      assignKid.mutate({ batchId: newBatchId, kidId });
      onOpenChange(false);
    }
  }

  function confirmMove() {
    if (pendingBatchId === null) return;
    assignKid.mutate({ batchId: pendingBatchId, kidId });
    setPendingBatchId(null);
    onOpenChange(false);
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign {kidFirstName} to a Batch</DialogTitle>
          </DialogHeader>
          <BatchCombobox
            batches={batches}
            value={currentBatchId ? String(currentBatchId) : undefined}
            onValueChange={handleChange}
            placeholder={currentBatchName ?? "Select a batch"}
            testId="assign-batch-select"
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={pendingBatchId !== null} onOpenChange={(o) => !o && setPendingBatchId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move player to a new batch?</AlertDialogTitle>
            <AlertDialogDescription>
              {kidFirstName} is currently in {currentBatchName}. Move to{" "}
              {batches.find((b) => b.id === pendingBatchId)?.name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingBatchId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMove}>Move</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
